'use client'

import type { Item } from 'analytics/utils/getEECItems/getEECItems'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { cx } from 'utils/cx'
import { env } from 'utils/envManager'
import { ClientLink } from 'utils/link/client'

import { AnalyticSelectItem } from '../../analytics/AnalyticSelectItem/AnalyticSelectItem'
import { dataTestIds } from '../../constants/dataTestIds/dataTestIds'
import { useColors } from '../../contexts/ColorsProvider/ColorsProvider'
import type { CommonProps } from '../../types/CommonProps'
import { getProductUrl } from '../../utils/getProductUrl/getProductUrl'
import { ColorItemLegacy } from './ColorItem/ColorItemLegacy'

import styles from './ColorsSelectorLegacy.module.scss'
import fukkuClasses from 'fukku/styles/classes.module.scss'

export type ColorsSelectorProps = Pick<CommonProps, 'product' | 'look'> & {
	readonly searchParams: Record<string, string>
	readonly className?: string
}

const COLOR_SELECTOR_TITLE_ID = 'product.colorsSelector'

export function ColorsSelectorLegacy({
	product: { url, id },
	look,
	searchParams,
	className,
}: ColorsSelectorProps): React.ReactNode {
	const { t } = useLabels()
	const {
		country: { countryISO, languageISO },
	} = useMasterData()

	const { colors, selectedColor } = useColors()

	return (
		<div
			className={cx(styles.colorsSelector, className)}
			data-testid={dataTestIds['pdp.productInfo.colorSelector']}
		>
			<p id={COLOR_SELECTOR_TITLE_ID} className={fukkuClasses.srOnly}>
				{t('accessibility.pdp.addColor.selector.noColor')}
			</p>
			<ul
				className={styles.colorsList}
				aria-describedby={COLOR_SELECTOR_TITLE_ID}
			>
				{colors.map((color) => {
					const isSelected = color.id === selectedColor.id
					const productUrl = getProductUrl({
						color,
						id,
						url,
						countryISO,
						languageISO,
						searchParams,
					})
					const selectedLabel = replacePlaceholders(
						t('accessibility.pdp.addColor.selector.withColor'),
						{ 1: color.label }
					)

					const item: Item = {
						colorId: color.id,
						productId: id,
						lookId: look.id,
						quantity: 1,
						index: 1,
					}
					return (
						<li
							key={color.id}
							data-testid={dataTestIds['pdp.colorSelector.link.fn'](color.id)}
						>
							{!isSelected ? (
								<AnalyticSelectItem
									selectItemPayload={{
										item,
									}}
								>
									<ClientLink
										href={`${env.NEXT_PUBLIC_CURRENT_ORIGIN}${productUrl}`}
										className={styles.link}
										key={color.id}
										isLocalizedPath
									>
										<ColorItemLegacy
											disabled={!color.isSellable}
											imageAlt={selectedLabel}
											imageSrc={color.bulletImg}
										/>
									</ClientLink>
								</AnalyticSelectItem>
							) : (
								<ColorItemLegacy
									disabled={!color.isSellable}
									imageAlt={selectedLabel}
									imageSrc={color.bulletImg}
									bordered
								/>
							)}
						</li>
					)
				})}
			</ul>
			<p
				className={styles.label}
				aria-hidden
				data-testid={dataTestIds['pdp.productInfo.colorSelector.label']}
			>
				{selectedColor.label}
			</p>
		</div>
	)
}
